<template>

  <div class="hflex">
    <creating
      v-if="creating"
      height="100%"
      style="width: 100%;" />
    <template v-else>
      <!-- sidebar: the conversations list -->
      <div
        class="messaging-sidebar border-right"
        :class="{ 'is-hidden-mobile': showContent, 'is-slim': fromCandidate }">
        <ConversationSidebar
          :slim="fromCandidate"
          :conversations="conversations"
          :loading="loadingConversations"
          :loading-more="loadingMore"
          :has-more="hasMore"
          :selected-id="selectedId"
          :filters="filters"
          :facets="facets"
          :search="search"
          @update:filters="changeFilters"
          @update:search="changeSearch"
          @select="goToConversation"
          @new-conversation="goToNewConversation"
          @load-more="loadMore"
          @removed="onConversationDeleted" />
      </div>

      <!-- main: the open conversation, or the form when there is none -->
      <div
        class="hflex-col-1"
        :class="{ 'is-hidden-mobile': !showContent }">
        <Conversation
          v-if="showConversation"
          :key="selectedId"
          :conversation="selectedConversation"
          :channels="channels"
          :users="users"
          :agent-enabled="agentEnabled"
          :loading="loadingConversation"
          :show-contact="!fromCandidate"
          @updated="onConversationUpdated"
          @deleted="onConversationDeleted" />
        <NewConversation
          v-else
          :contacts="contacts"
          :contact-name="candidateName"
          :candidate-id="candidateId"
          :context-key="contextKey"
          :can-go-back="!!firstConversationId"
          @back="goToFirstConversation"
          @created="onConversationCreated" />
      </div>
    </template>

  </div>

</template>

<script>
import ConversationSidebar from './sidebar/ConversationSidebar.vue'
import Conversation from './conversation/Conversation.vue'
import NewConversation from './NewConversation.vue'

export default {
  name: 'Messaging',
  components: {
    ConversationSidebar,
    Conversation,
    NewConversation
  },
  props: {
    /*
    candidateId
    set when the component is embedded somewhere that already has a candidate
    (its profile, for instance): the list is then restricted to that candidate
    conversations and the whole ui gets narrower
    */
    candidateId: {
      type: String,
      default: null
    },
    /*
    contacts
    addresses already known by whoever embeds the component (the candidate
    phones, for instance), offered as shortcuts on the new conversation form:
    [{ type: 'whatsapp', contact_address: '+5511990000000' }, ...]
    */
    contacts: {
      type: Array,
      default: () => []
    },
    // prefills the name on the new conversation form
    candidateName: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      creating: true,
      loadingConversations: false,
      loadingMore: false,
      loadingConversation: false,
      /*
      /messaging opens the most recent conversation. it is armed on the entry
      and consumed by the first list that arrives: rearming it on every load
      would drag the user into a conversation when he goes back to the list or
      changes a filter
      */
      pendingFirstConversation: false,
      filters: {
        // starts on the logged in user's conversations, but a candidate has
        // few of them: no reason to hide the ones assigned to someone else
        assigned: this.candidateId ? 'all' : 'me', // 'all' | 'me' | 'unassigned'
        agent: 'any', // 'any' | 'on' | 'off'
        unreplied: false,
        candidate_id: this.candidateId || null
      },
      search: '',
      /*
      embedded (candidate) mode has no routes of its own: the selection and the
      new conversation form live in the component instead of in the url
      */
      innerSelectedId: null,
      innerNewConversation: false,
      pollTimer: null,
      pollInterval: 3000 // 15000
    }
  },
  computed: {
    account () {
      return this.$store.getters.getAccount
    },
    /*
    agentEnabled
    the account wide switches of the conversational agent, one per audience
    (the account settings page). they gate what a conversation can do: with the
    audience off the agent never answers, so the conversation own switch has
    nothing to turn on
    */
    agentEnabled () {
      const candidateAgentEnabled = !!(this.account && this.account.conversational_agent && this.account.conversational_agent.candidate && this.account.conversational_agent.candidate.is_enabled)
      return {
        candidateAgentEnabled
      }
    },
    fromCandidate () {
      return !!this.candidateId
    },
    /*
    contextKey
    the messaging page and the candidate profile can be on the screen at the
    same time: each one keeps its own list in the store
    */
    contextKey () {
      return this.fromCandidate ? 'conversationContext2' : 'conversationContext1'
    },
    conversations () {
      return this.$store.getters.getConversations(this.contextKey)
    },
    paginate () {
      return this.$store.getters.getConversationsPaginate(this.contextKey) || {}
    },
    // totais por filtro de responsável, devolvidos pela própria busca
    facets () {
      return this.$store.getters.getConversationsFacets(this.contextKey) || {}
    },
    hasMore () {
      return !!(this.paginate.page && this.paginate.pages && this.paginate.page < this.paginate.pages)
    },
    channels () {
      return this.$store.getters.getMessagingChannels
    },
    users () {
      return this.$store.getters.getMembers
    },
    me () {
      return this.$store.getters.getMe
    },
    unreadCount () {
      return this.conversations.filter((conversation) => { return conversation && conversation.is_read === false }).length
    },
    selectedId () {
      if (this.fromCandidate) {
        return this.innerSelectedId
      }
      return this.$route.params.conversationId || null
    },
    isNewConversation () {
      if (this.fromCandidate) {
        return this.innerNewConversation
      }
      return this.$route.name === 'messaging.new'
    },
    // the form takes the whole column whenever no conversation is open
    showConversation () {
      return !!this.selectedId && !this.isNewConversation
    },
    firstConversationId () {
      const first = this.conversations[0]
      return first ? first._id : null
    },
    // on mobile the sidebar and the content column take turns
    showContent () {
      return !!this.selectedId || this.isNewConversation
    },
    selectedConversation () {
      if (!this.selectedId) {
        return null
      }
      return this.$store.getters.getConversationById(this.selectedId)
    }
  },
  metaInfo () {
    // embedded, the page title belongs to whoever is hosting the component
    if (this.fromCandidate) {
      return {}
    }
    return {
      title: this.$t('Messaging')
    }
  },
  watch: {
    selectedId (id) {
      if (id) {
        this.loadConversation(id)
      }
    }
  },
  created () {
    // landing on /messaging: the first conversation of the list is opened
    this.pendingFirstConversation = !this.selectedId && !this.isNewConversation
    this.loadData()
    this.startPolling()
  },
  beforeDestroy () {
    this.stopPolling()
    // the next one to open this context (another candidate, for instance) must
    // not see this list while its own is loading
    this.$store.dispatch('resetConversationsContext', this.contextKey)
  },
  methods: {
    /*
    startPolling
    schedules the next silent refresh (recursive setTimeout avoids overlap)
    */
    startPolling () {
      this.pollTimer = setTimeout(this.poll, this.pollInterval)
    },
    /*
    stopPolling
    */
    stopPolling () {
      clearTimeout(this.pollTimer)
    },
    /*
    poll
    refreshes the conversations and the open conversation messages so the
    ORM models stay up to date (new incoming messages, etc.)
    */
    poll () {
      const tasks = [
        this.$store.dispatch('refreshSelectConversations', this.contextKey).catch(() => {})
      ]

      if (this.selectedId) {
        tasks.push(this.$store.dispatch('selectMessages', { conversationId: this.selectedId }).catch(() => {}))
      }

      Promise.all(tasks).finally(() => {
        this.pollTimer = setTimeout(this.poll, this.pollInterval)
      })
    },
    /*
    loadData
    initial load: channels, members and the conversations list
    */
    loadData () {
      this.$store.dispatch('selectMessagingChannels').catch(() => {})
      this.$store.dispatch('selectMembers', { active: true }).catch(() => {})
      this.loadConversations()

      if (this.selectedId) {
        this.loadConversation(this.selectedId)
      }
    },
    /*
    loadConversations
    */
    loadConversations () {
      this.loadingConversations = true

      this.$store
      .dispatch('selectConversations', { contextKey: this.contextKey, filters: this.buildFilters() })
      .then(() => {
        this.openFirstConversation()
      })
      .catch(() => {})
      .finally(() => {
        this.loadingConversations = this.creating = false
      })
    },
    /*
    openFirstConversation
    /messaging has no conversation of its own: it opens the first one of the
    list (the most recent). replace, so the back button does not fall into the
    redirect again
    */
    openFirstConversation () {
      if (!this.pendingFirstConversation) {
        return
      }
      this.pendingFirstConversation = false

      if (!this.firstConversationId || this.selectedId || this.isNewConversation) {
        return
      }
      if (this.fromCandidate) {
        this.innerSelectedId = this.firstConversationId
        return
      }
      this.$router.replace({ name: 'messaging.conversation', params: { conversationId: this.firstConversationId } })
    },
    /*
    loadMore
    */
    loadMore () {
      if (this.loadingMore || !this.hasMore) {
        return
      }
      this.loadingMore = true

      this.$store
      .dispatch('loadMoreConversations', { contextKey: this.contextKey, page: this.paginate.page + 1 })
      .catch(() => {})
      .finally(() => {
        this.loadingMore = false
      })
    },
    /*
    loadConversation
    ensures the selected conversation is available
    */
    loadConversation (id) {
      if (this.$store.getters.getConversationById(id)) {
        this.$store.dispatch('selectConversation', id)
        return
      }
      this.loadingConversation = true

      this.$store
      .dispatch('selectConversation', id)
      .catch(() => {})
      .finally(() => {
        this.loadingConversation = false
      })
    },
    /*
    buildFilters
    translates the UI filter/search into query params
    */
    buildFilters () {
      const filters = {}

      if (this.search) {
        filters.search = this.search
      }

      /*
      assigned:
      'all' -> sem filtro
      'me' -> as do usuário logado
      'unassigned' -> as que ninguém está tocando: sem responsável e com o
      agente desligado (o agente vai junto, não é escolha do usuário)
      */
      if (this.filters.assigned === 'me') {
        if (this.me && this.me._id) {
          filters.assigned_user_id = this.me._id
        }
      } else if (this.filters.assigned === 'unassigned') {
        filters.assigned_user_id = null
        filters.is_agent_enabled = false
      }

      // agent: 'any' -> sem filtro; só vale fora de 'unassigned'
      if (this.filters.assigned !== 'unassigned') {
        if (this.filters.agent === 'on') {
          filters.is_agent_enabled = true
        } else if (this.filters.agent === 'off') {
          filters.is_agent_enabled = false
        }
      }

      // unreplied: only sent when enabled
      if (this.filters.unreplied) {
        filters.unreplied = true
      }

      if (this.filters.candidate_id) {
        filters.candidate_id = this.filters.candidate_id
      }

      return filters
    },
    /*
    changeFilters
    */
    changeFilters (filters) {
      // the candidate is not a filter the user can drop
      this.filters = { ...filters, candidate_id: this.candidateId || null }
      this.loadConversations()
    },
    /*
    changeSearch
    */
    changeSearch (search) {
      this.search = search
      this.loadConversations()
    },
    /*
    goToConversation
    */
    goToConversation (id) {
      // already open, unless the form is covering it
      if (id === this.selectedId && !this.isNewConversation) {
        return
      }
      if (this.fromCandidate) {
        this.innerNewConversation = false
        this.innerSelectedId = id
        return
      }
      this.$router.push({ name: 'messaging.conversation', params: { conversationId: id } })
    },
    /*
    goToFirstConversation
    leaves the form and opens the most recent conversation of the list
    */
    goToFirstConversation () {
      if (!this.firstConversationId) {
        return
      }
      this.goToConversation(this.firstConversationId)
    },
    /*
    goToList
    */
    goToList () {
      if (this.fromCandidate) {
        this.innerNewConversation = false
        this.innerSelectedId = null
        return
      }
      this.$router.push({ name: 'messaging' })
    },
    /*
    goToNewConversation
    */
    goToNewConversation () {
      if (this.isNewConversation) {
        return
      }
      if (this.fromCandidate) {
        this.innerNewConversation = true
        return
      }
      this.$router.push({ name: 'messaging.new' })
    },
    /*
    onConversationCreated
    opens the conversation of the contact just created
    */
    onConversationCreated (conversation) {
      if (conversation && conversation._id) {
        if (this.fromCandidate) {
          this.innerNewConversation = false
          this.innerSelectedId = conversation._id
          return
        }
        this.$router.replace({ name: 'messaging.conversation', params: { conversationId: conversation._id } })
        return
      }
      this.goToList()
    },
    /*
    onConversationUpdated
    */
    onConversationUpdated () {
      // conversation entity is already updated in the store; nothing else to do
    },
    /*
    onConversationDeleted
    */
    onConversationDeleted () {
      this.goToList()
    }
  }
}
</script>

<style scoped>
/* fixed 300px column: flex-basis instead of min-width so the content cannot
   stretch it, and min-width 0 so the default auto minimum does not either */
.messaging-sidebar {
  flex: 0 0 300px;
  min-width: 0;
}

/* embedded in a candidate: just the numbered circles and the + button */
.messaging-sidebar.is-slim {
  flex: 0 0 50px;
}
</style>



// WEBPACK FOOTER //
// src/components/Messaging/Messaging.vue